import React, { useState, useEffect, useMemo } from 'react';
import ProfileAchievementsWidget from './ProfileAchievementsWidget';
import ProfileStatsWidget from './ProfileStatsWidget';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { fetchUserActivityStats } from '../../utils/profileStatsService';
import { syncBadgeUnlockNotifications } from '../../utils/badgeUnlockNotifications';
import { FriendsWidget } from './friends';
import AccountPlanWidget from './AccountPlanWidget';
import { RoleBadge } from '../badges';
import Loading from '../layout/Loading';
import {
  ProfileActionSettingsIcon,
  ProfileAvatarEditIcon,
  ProfileAvatarPlaceholderIcon,
} from './ProfileDashIcons';

function ProfileInfoView({ onEdit, onSettings, onNavigateToMyEvents, onNavigateToDiscover }) {
  const { t, i18n } = useTranslation(['profile', 'shared']);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    participations: { upcoming: 0, completed: 0 },
    organized: 0,
    friendsCount: 0,
    favoriteCategory: null,
    uniqueCities: 0,
    completedEvents: 0,
    organizedEvents: 0,
    isBetaTester: false,
    preLaunchSubscriber: false,
    supporterDonor: false,
  });

  useEffect(() => {
    loadUserData();

    const handleProfileUpdate = () => {
      loadUserData();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setStatsLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      const next = await fetchUserActivityStats(user.id);
      if (!cancelled) {
        setStats(next);
        setStatsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !profile || statsLoading) return;
    const role = profile.user_role || 'user';
    syncBadgeUnlockNotifications({
      userId: user.id,
      userRole: role,
      stats,
    });
  }, [user?.id, profile, statsLoading, stats]);

  const loadUserData = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (authUser) {
        setUser(authUser);

        const { data: userProfile, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .single();

        if (error) {
          console.error('Error loading profile:', error);
        } else {
          setProfile(userProfile);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const userRole = profile?.user_role || 'user';

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const displayName = useMemo(() => {
    if (!profile) return t('profile.info.nameNotSet');
    return (
      profile.nickname
      || (profile.first_name && profile.last_name
        ? `${profile.first_name} ${profile.last_name}`
        : profile.name || t('profile.info.nameNotSet'))
    );
  }, [profile, t]);

  if (loading) {
    return <Loading size="small" />;
  }

  return (
    <div className="profile-info-view">
      <div className="profile-card">
        <header className="p-dash-hero">
          <div className="p-dash-hero-main">
            <div className="p-dash-avatar-wrap">
              {profile?.avatar_url ? (
                <img
                  src={getAvatarUrl(profile.avatar_url, 200)}
                  alt={t('profile.info.avatarAlt')}
                  className="p-dash-avatar-img"
                />
              ) : (
                <div className="p-dash-avatar-placeholder" aria-hidden>
                  <ProfileAvatarPlaceholderIcon />
                </div>
              )}
              {onEdit && (
                <button
                  type="button"
                  className="p-dash-avatar-edit"
                  onClick={onEdit}
                  title={t('profile.info.editAvatar')}
                  aria-label={t('profile.info.editAvatar')}
                >
                  <ProfileAvatarEditIcon />
                </button>
              )}
            </div>
            <div className="p-dash-hero-text">
              <p className="p-dash-kicker">{t('profile.dashboard.kicker', 'Twój profil')}</p>
              <h2 className="p-dash-display-name">{displayName}</h2>
              <div className="p-dash-hero-chips">
                {profile?.city && (
                  <span className="p-dash-chip">📍 {profile.city}</span>
                )}
                <RoleBadge userRole={profile?.user_role} size="small" />
              </div>
              {profile?.created_at && (
                <p className="p-dash-since">
                  {t('profile.dashboard.memberSince', 'Na PlanGoNOW od {{date}}', {
                    date: new Date(profile.created_at).toLocaleDateString(locale),
                  })}
                </p>
              )}
            </div>
          </div>
          <div className="p-dash-hero-actions">
            {onEdit && (
              <button type="button" className="action-button" onClick={onEdit}>
                <ProfileAvatarEditIcon className="p-dash-action-icon-svg" size={18} />
                {t('profile.subTabs.edit')}
              </button>
            )}
            {onSettings && (
              <button
                type="button"
                className="action-button action-button--settings"
                onClick={onSettings}
              >
                <ProfileActionSettingsIcon />
                {t('profile.subTabs.settings')}
              </button>
            )}
          </div>
        </header>

        <div className="p-dash-split">
          <section className="p-dash-panel" aria-labelledby="p-dash-facts-heading">
            <h3 id="p-dash-facts-heading" className="p-dash-section-title">
              {t('profile.dashboard.factsTitle', 'Dane konta')}
            </h3>
            <dl className="p-dash-dl">
              <div className="p-dash-dl-row">
                <dt>{t('profile.info.email')}</dt>
                <dd>{user?.email}</dd>
              </div>
              {(profile?.first_name || profile?.last_name) && (
                <div className="p-dash-dl-row">
                  <dt>{t('profile.info.firstName')}</dt>
                  <dd>{[profile?.first_name, profile?.last_name].filter(Boolean).join(' ')}</dd>
                </div>
              )}
              {profile?.nickname && (
                <div className="p-dash-dl-row">
                  <dt>{t('profile.info.nickname')}</dt>
                  <dd>{profile.nickname}</dd>
                </div>
              )}
              {!profile?.first_name && !profile?.last_name && (
                <div className="p-dash-dl-row">
                  <dt>{t('profile.info.name')}</dt>
                  <dd>{profile?.name || t('profile.info.nameNotSet')}</dd>
                </div>
              )}
              {profile?.city && (
                <div className="p-dash-dl-row">
                  <dt>{t('profile.info.city')}</dt>
                  <dd>{profile.city}</dd>
                </div>
              )}
              {profile?.gender && (
                <div className="p-dash-dl-row">
                  <dt>{t('profile.info.gender')}</dt>
                  <dd>{t(`profile.info.genderOptions.${profile.gender}`)}</dd>
                </div>
              )}
              {profile?.birth_date && (
                <div className="p-dash-dl-row">
                  <dt>{t('profile.info.birthDate')}</dt>
                  <dd>
                    {new Date(profile.birth_date).toLocaleDateString(locale)}
                    {' '}
                    (
                    {calculateAge(profile.birth_date)}
                    {' '}
                    {t('profile.info.yearsOld')}
                    )
                  </dd>
                </div>
              )}
            </dl>
          </section>

          <section className="p-dash-panel p-dash-panel--story" aria-labelledby="p-dash-story-heading">
            <h3 id="p-dash-story-heading" className="p-dash-section-title">
              {t('profile.dashboard.storyTitle', 'O Tobie')}
            </h3>
            {profile?.description ? (
              <blockquote className="p-dash-quote">
                {profile.description}
              </blockquote>
            ) : (
              <p className="p-dash-story-empty">
                {t('profile.dashboard.storyEmpty', 'Dodaj krótki opis w edycji profilu — inni zobaczą Cię lepiej na wydarzeniach.')}
              </p>
            )}
          </section>
        </div>
      </div>

      <div className="profile-dashboard-widgets">
        <ProfileStatsWidget
          stats={stats}
          statsLoading={statsLoading}
          onNavigateToMyEvents={onNavigateToMyEvents}
          onNavigateToDiscover={onNavigateToDiscover}
        />
        <ProfileAchievementsWidget stats={stats} userRole={profile?.user_role} />
      </div>

      <div className="profile-bottom-widgets">
        <div className="profile-bottom-widgets-main">
          <FriendsWidget />
        </div>
        <AccountPlanWidget userRole={userRole} />
      </div>
    </div>
  );
}

export default ProfileInfoView;
